import {
    isMobileDevPaywallEnabled,
    REVENUECAT_ABSITE_MONTHLY_PRODUCT_ID,
    MOBILE_BILLING_REASONS,
    REVENUECAT_ABSITE_TRIAL_PERIOD_DAYS
} from '../constants/mobileBilling';
import { api } from './apiService';
import {
    configureRevenueCat,
    findRevenueCatPackageByProductId,
    getRevenueCatOfferings,
    purchaseRevenueCatPackage,
    restoreRevenueCatPurchases
} from './revenueCatService';

// Same native-only lazy-load pattern as revenueCatService: the plugin module is
// only imported when a call site actually needs it, so web bundles/renders never
// touch @revenuecat/purchases-capacitor.
const loadRevenueCatModuleDefault = () => import('@revenuecat/purchases-capacitor');

// INTRO_ELIGIBILITY_STATUS.INTRO_ELIGIBILITY_STATUS_ELIGIBLE in the installed
// @revenuecat/purchases-capacitor. Fallback only — the enum from the loaded
// module wins when present, and anything that isn't an exact ELIGIBLE match
// resolves to "no trial" (fail-safe).
const APPLE_INTRO_ELIGIBILITY_STATUS_ELIGIBLE = 2;

// Runbook qlearn-unified#119 MUST-a: on iOS, Apple's intro-offer eligibility
// (surfaced via RevenueCat checkTrialOrIntroductoryPriceEligibility) is the
// source of truth for whether the paywall may promise a free week. The server's
// per-QLearn-account getTrialEligibility cannot see reinstalls or a second
// QLearn account on the same Apple ID — Apple bills such users $59 immediately
// even though StoreKit shows no intro offer. Fail-safe mapping: only an exact
// ELIGIBLE status returns true; INELIGIBLE/UNKNOWN/no-offer/plugin-unavailable/
// any error returns false, so the CTA never promises a trial when uncertain.
// Never throws.
export const getAbsiteAppleTrialEligibility = async ({
    appUserID,
    productId = REVENUECAT_ABSITE_MONTHLY_PRODUCT_ID,
    loadRevenueCatModule = loadRevenueCatModuleDefault
} = {}) => {
    // Local iOS preview: the store is disabled and there is no plugin bridge,
    // but the paywall must still exercise the trial presentation (same
    // short-circuit as every other dev-paywall path in this service).
    if (isMobileDevPaywallEnabled()) {
        return true;
    }

    try {
        // checkTrialOrIntroductoryPriceEligibility rejects unless configure has
        // run; configureRevenueCat also fails closed on web/non-native runtimes.
        const configuration = await configureRevenueCat({ appUserID });

        if (!configuration?.configured) {
            return false;
        }

        const { INTRO_ELIGIBILITY_STATUS, Purchases } = await loadRevenueCatModule();
        const eligibility = await Purchases.checkTrialOrIntroductoryPriceEligibility({
            productIdentifiers: [productId]
        });
        const status = eligibility?.[productId]?.status;
        const eligibleStatus =
            INTRO_ELIGIBILITY_STATUS?.INTRO_ELIGIBILITY_STATUS_ELIGIBLE ??
            APPLE_INTRO_ELIGIBILITY_STATUS_ELIGIBLE;

        return status === eligibleStatus;
    } catch {
        return false;
    }
};

const failedEligibility = (productId, error) => ({
    productId,
    mapped: false,
    purchaseAllowed: false,
    hasActiveAccess: false,
    reason: MOBILE_BILLING_REASONS.ELIGIBILITY_CHECK_FAILED,
    error: error?.message || error?.errMsg?.message || error?.errMsg || 'Unable to verify purchase eligibility'
});

export const getAbsiteMobilePurchaseEligibility = async (
    productId = REVENUECAT_ABSITE_MONTHLY_PRODUCT_ID
) => {
    if (isMobileDevPaywallEnabled()) {
        return {
            productId,
            mapped: true,
            purchaseAllowed: true,
            hasActiveAccess: false,
            reason: MOBILE_BILLING_REASONS.DEV_PAYWALL_STORE_DISABLED,
            mobileDevPaywall: true
        };
    }

    try {
        const eligibility = await api.getRevenueCatPurchaseEligibility(productId);

        if (!eligibility || typeof eligibility.purchaseAllowed !== 'boolean' || eligibility.status || eligibility.errStatus) {
            return failedEligibility(productId, eligibility);
        }

        return {
            productId,
            ...eligibility
        };
    } catch (error) {
        return failedEligibility(productId, error);
    }
};

// A user tapping "Cancel" on the Apple purchase sheet rejects purchasePackage.
// @revenuecat/purchases-capacitor flags this as userCancelled (and/or a
// PURCHASE_CANCELLED_ERROR code) — it is a normal action, not a failure.
export const isUserCancelledPurchaseError = (error) => (
    error?.userCancelled === true ||
    error?.code === 'PURCHASE_CANCELLED_ERROR' ||
    error?.code === '1' ||
    /purchase.?cancell?ed|user.?cancell?ed/i.test(
        error?.message || error?.errMsg?.message || error?.errMsg || ''
    )
);

const failedPurchase = ({
    productId,
    stage,
    reason,
    eligibility,
    configuration,
    error
}) => ({
    purchased: false,
    productId,
    stage,
    reason,
    eligibility,
    configuration,
    ...(error ? { error: error?.message || error?.errMsg?.message || error?.errMsg || String(error) } : {})
});

// Watchdog for the unbounded native awaits in the billing chain: the native
// Purchases.purchasePackage promise hangs forever when StoreKit never presents
// a sheet (no sandbox Apple ID signed in on a test build, ASC product still
// propagating). The timer cannot cancel the native call — a late completion
// is delivered through onLateCompletion (below) and Restore Purchases also
// reconciles — but the UI must never spin indefinitely with no way out
// (Akshay device finding, 2026-07-07). 120s: password entry / card update /
// Ask-to-Buy / SCA can legitimately hold a REAL sheet open well past a
// minute, and a premature "didn't start" against a live sheet is worse than
// a slow timeout against a hung one.
const PURCHASE_SHEET_TIMEOUT_MS = 120000;

const PURCHASE_TIMED_OUT_SENTINEL = Symbol('purchase-timed-out');

// Every pre-sheet dependency (server eligibility, RC configure, offerings /
// restore configure) gets its own bound: a hung fetch, lazy plugin import, or
// bridge call must settle the customer's spinner with a typed failure instead
// of hanging forever (gate leg GPT-5.6 finding 4). The sheet itself keeps the
// longer PURCHASE_SHEET_TIMEOUT_MS + late-completion reconciliation; these
// stages never charge anyone, so timing out and retrying is always safe.
const SETUP_STAGE_TIMEOUT_MS = 30000;

const raceWithTimeout = (promise, timeoutMs) => {
    let timer;
    return Promise.race([
        promise,
        new Promise((resolve) => {
            timer = setTimeout(() => resolve(PURCHASE_TIMED_OUT_SENTINEL), timeoutMs);
        })
    ]).finally(() => clearTimeout(timer));
};

// Round-2 finding 1: the consent disclosure must render the LOCALIZED price
// and the ACTUAL intro offer of the exact StoreKit product being bought —
// backend rows carry web pricing and an unverified trial length. This fetch
// binds the native paywall/checkout copy to the RevenueCat package BEFORE
// confirmation; callers FAIL CLOSED (no confirm) when it cannot resolve.
// Every stage is bounded (finding 4 discipline).
export const getAbsiteMobilePackagePricing = async ({
    appUserID,
    productId = REVENUECAT_ABSITE_MONTHLY_PRODUCT_ID,
    setupTimeoutMs = SETUP_STAGE_TIMEOUT_MS
} = {}) => {
    if (isMobileDevPaywallEnabled()) {
        // Local preview has no StoreKit — the dev plan's static figures ARE
        // the disclosure, flagged so the UI knows the source.
        return {
            ready: true,
            source: 'dev-paywall',
            productId,
            priceString: '$59.00',
            price: 59,
            currencyCode: 'USD',
            introTrialDays: REVENUECAT_ABSITE_TRIAL_PERIOD_DAYS,
            billingCadence: '/month'
        };
    }

    const configuration = await raceWithTimeout(
        configureRevenueCat({ appUserID }), setupTimeoutMs
    );
    if (configuration === PURCHASE_TIMED_OUT_SENTINEL || !configuration?.configured) {
        return { ready: false, stage: 'configure', reason: configuration?.reason || MOBILE_BILLING_REASONS.SETUP_TIMED_OUT };
    }

    let offerings;
    try {
        offerings = await raceWithTimeout(getRevenueCatOfferings(), setupTimeoutMs);
    } catch (error) {
        return { ready: false, stage: 'offerings', reason: MOBILE_BILLING_REASONS.REVENUECAT_OFFERINGS_FAILED, error };
    }
    if (offerings === PURCHASE_TIMED_OUT_SENTINEL) {
        return { ready: false, stage: 'offerings', reason: MOBILE_BILLING_REASONS.SETUP_TIMED_OUT };
    }

    const revenueCatPackage = findRevenueCatPackageByProductId(offerings, productId);
    if (!revenueCatPackage) {
        return { ready: false, stage: 'package', reason: MOBILE_BILLING_REASONS.PRODUCT_NOT_AVAILABLE };
    }

    const product = revenueCatPackage.product || {};
    const intro = product.introPrice || null;
    // periodUnit DAY/WEEK/MONTH + periodNumberOfUnits -> days; only a FREE
    // intro offer ($0) is a trial we may promise.
    const UNIT_DAYS = { DAY: 1, WEEK: 7, MONTH: 30, YEAR: 365 };
    const introUnits = Number(intro?.periodNumberOfUnits);
    const introUnitDays = UNIT_DAYS[String(intro?.periodUnit || '').toUpperCase()] || 0;
    const introIsFree = intro ? Number(intro.price) === 0 : false;
    const introTrialDays = introIsFree && Number.isFinite(introUnits) && introUnitDays > 0
        ? introUnits * introUnitDays
        : 0;

    const durationDays = Number(product.subscriptionPeriodNumberOfUnits) *
        (UNIT_DAYS[String(product.subscriptionPeriodUnit || '').toUpperCase()] || 0);
    const billingCadence = durationDays >= 28 && durationDays <= 31
        ? '/month'
        : (durationDays >= 365 && durationDays <= 366 ? '/year' : '/month');

    return {
        ready: true,
        source: 'storekit',
        productId,
        priceString: product.priceString || null,
        price: Number(product.price),
        currencyCode: product.currencyCode || null,
        introTrialDays,
        billingCadence
    };
};

export const purchaseAbsiteMobileSubscription = async ({
    appUserID,
    productId = REVENUECAT_ABSITE_MONTHLY_PRODUCT_ID,
    purchaseTimeoutMs = PURCHASE_SHEET_TIMEOUT_MS,
    setupTimeoutMs = SETUP_STAGE_TIMEOUT_MS,
    // Called if the native purchase COMPLETES after the watchdog already
    // returned PURCHASE_TIMED_OUT (slow-but-real sheet: password entry,
    // Ask to Buy, SCA). Receives { purchased: true, late: true, productId,
    // purchase } so the UI can flip to its success state instead of leaving
    // a charged user staring at a failure message.
    onLateCompletion
} = {}) => {
    if (isMobileDevPaywallEnabled()) {
        return {
            purchased: false,
            productId,
            stage: 'dev-paywall',
            reason: MOBILE_BILLING_REASONS.DEV_PAYWALL_STORE_DISABLED,
            mobileDevPaywall: true
        };
    }

    const eligibility = await raceWithTimeout(
        getAbsiteMobilePurchaseEligibility(productId), setupTimeoutMs
    );

    if (eligibility === PURCHASE_TIMED_OUT_SENTINEL) {
        return failedPurchase({
            productId,
            stage: 'eligibility',
            reason: MOBILE_BILLING_REASONS.SETUP_TIMED_OUT
        });
    }

    if (!eligibility.purchaseAllowed) {
        return failedPurchase({
            productId,
            stage: 'eligibility',
            reason: eligibility.reason,
            eligibility
        });
    }

    const configuration = await raceWithTimeout(
        configureRevenueCat({ appUserID }), setupTimeoutMs
    );

    if (configuration === PURCHASE_TIMED_OUT_SENTINEL) {
        return failedPurchase({
            productId,
            stage: 'configure',
            reason: MOBILE_BILLING_REASONS.SETUP_TIMED_OUT,
            eligibility
        });
    }

    if (!configuration.configured) {
        return failedPurchase({
            productId,
            stage: 'configure',
            reason: configuration.reason,
            eligibility,
            configuration
        });
    }

    let offerings;

    try {
        offerings = await raceWithTimeout(getRevenueCatOfferings(), setupTimeoutMs);
        if (offerings === PURCHASE_TIMED_OUT_SENTINEL) {
            return failedPurchase({
                productId,
                stage: 'offerings',
                reason: MOBILE_BILLING_REASONS.SETUP_TIMED_OUT,
                eligibility,
                configuration
            });
        }
    } catch (error) {
        return failedPurchase({
            productId,
            stage: 'offerings',
            reason: MOBILE_BILLING_REASONS.REVENUECAT_OFFERINGS_FAILED,
            eligibility,
            configuration,
            error
        });
    }

    const revenueCatPackage = findRevenueCatPackageByProductId(offerings, productId);

    if (!revenueCatPackage) {
        return failedPurchase({
            productId,
            stage: 'package',
            reason: MOBILE_BILLING_REASONS.PRODUCT_NOT_AVAILABLE,
            eligibility,
            configuration
        });
    }

    try {
        const nativePurchase = purchaseRevenueCatPackage(revenueCatPackage);
        const purchase = await raceWithTimeout(nativePurchase, purchaseTimeoutMs);

        if (purchase === PURCHASE_TIMED_OUT_SENTINEL) {
            // Stop waiting, but keep listening: a late RESOLUTION is a real,
            // charged purchase and must reach the UI's success path; a late
            // rejection is swallowed so it can't fire an unhandled rejection.
            nativePurchase.then(
                (latePurchase) => {
                    if (typeof onLateCompletion === 'function') {
                        onLateCompletion({
                            purchased: true,
                            late: true,
                            productId,
                            purchase: latePurchase
                        });
                    }
                },
                () => { }
            );
            return failedPurchase({
                productId,
                stage: 'purchase',
                reason: MOBILE_BILLING_REASONS.PURCHASE_TIMED_OUT,
                eligibility,
                configuration
            });
        }

        return {
            purchased: true,
            productId,
            stage: 'purchase',
            eligibility,
            configuration,
            revenueCatPackage,
            purchase
        };
    } catch (error) {
        // B1: a user cancel is not an error — return a benign, non-error result
        // so the UI shows no failure message.
        if (isUserCancelledPurchaseError(error)) {
            return {
                purchased: false,
                cancelled: true,
                productId,
                stage: 'purchase',
                reason: MOBILE_BILLING_REASONS.PURCHASE_CANCELLED,
                eligibility,
                configuration
            };
        }

        return failedPurchase({
            productId,
            stage: 'purchase',
            reason: MOBILE_BILLING_REASONS.REVENUECAT_PURCHASE_FAILED,
            eligibility,
            configuration,
            error
        });
    }
};

const failedRestore = ({
    stage,
    reason,
    configuration,
    error
}) => ({
    restored: false,
    stage,
    reason,
    configuration,
    ...(error ? { error: error?.message || error?.errMsg?.message || error?.errMsg || String(error) } : {})
});

export const restoreAbsiteMobilePurchases = async ({
    appUserID,
    restoreTimeoutMs = PURCHASE_SHEET_TIMEOUT_MS,
    setupTimeoutMs = SETUP_STAGE_TIMEOUT_MS
} = {}) => {
    if (isMobileDevPaywallEnabled()) {
        return {
            restored: false,
            stage: 'dev-paywall',
            reason: MOBILE_BILLING_REASONS.DEV_PAYWALL_STORE_DISABLED,
            mobileDevPaywall: true
        };
    }

    const configuration = await raceWithTimeout(
        configureRevenueCat({ appUserID }), setupTimeoutMs
    );

    if (configuration === PURCHASE_TIMED_OUT_SENTINEL) {
        return failedRestore({
            stage: 'configure',
            reason: MOBILE_BILLING_REASONS.RESTORE_TIMED_OUT
        });
    }

    if (!configuration.configured) {
        return failedRestore({
            stage: 'configure',
            reason: configuration.reason,
            configuration
        });
    }

    try {
        // Same watchdog as the purchase path: the timeout snackbar sends users
        // HERE, so Restore must not have the same infinite-hang mode. Restore
        // is idempotent — a timed-out attempt is simply retried.
        const nativeRestore = restoreRevenueCatPurchases();
        const restore = await raceWithTimeout(nativeRestore, restoreTimeoutMs);

        if (restore === PURCHASE_TIMED_OUT_SENTINEL) {
            nativeRestore.catch(() => { });
            return failedRestore({
                stage: 'restore',
                reason: MOBILE_BILLING_REASONS.RESTORE_TIMED_OUT,
                configuration
            });
        }

        // B2: restorePurchases() resolves even when there is nothing to restore.
        // Distinguish an actual restore (active entitlements) from an empty one
        // so the UI doesn't claim "Purchases restored" when nothing changed.
        const activeEntitlements = restore?.customerInfo?.entitlements?.active || {};
        const hasActiveEntitlements = Object.keys(activeEntitlements).length > 0;

        return {
            restored: hasActiveEntitlements,
            nothingToRestore: !hasActiveEntitlements,
            hasActiveEntitlements,
            ...(hasActiveEntitlements ? {} : { reason: MOBILE_BILLING_REASONS.NOTHING_TO_RESTORE }),
            stage: 'restore',
            configuration,
            restore
        };
    } catch (error) {
        return failedRestore({
            stage: 'restore',
            reason: MOBILE_BILLING_REASONS.REVENUECAT_RESTORE_FAILED,
            configuration,
            error
        });
    }
};
